import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';
import type { UserProfile } from '@/services/auth-service';

// Interface expandida com level e permissions (Fase 1-2 do RBAC)
export interface User {
  name: string;
  email: string;
  role: string;
  level: number;
  user_id: string;
  company_id: string | null;
  clinic_ids: string[];
  permissions: string[];
}

interface AuthContextType {
  isAuthenticated: boolean;
  accessToken: string | null;
  user: User | null;

  // Helpers de permission (Fase 5 — preferir estes)
  can: (resource: string, action: string) => boolean;
  canAny: (pairs: Array<[string, string]>) => boolean;

  // Aliases legacy (mantidos durante transição; prefira `can()`)
  isAdmin: boolean;
  isAdminLocal: boolean;
  canManageUsers: boolean;

  login: (token: string, profile?: UserProfile | null) => void;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const USER_STORAGE_KEY = 'auth_user';

/**
 * Reconstrói o User a partir do JWT quando o profile não está disponível
 * (ex: user atualizou a página antes do fluxo enriquecido estar ativo).
 * Sem `permissions[]` porque não vêm no JWT — vêm no body do /login/.
 */
function userFromToken(token: string): User | null {
  try {
    const decoded: any = jwtDecode(token);
    return {
      name: decoded.user_name || decoded.sub || 'Usuário',
      email: decoded.sub || '',
      role: decoded.role || '',
      level: typeof decoded.level === 'number' ? decoded.level : 999,
      user_id: decoded.user_id || '',
      company_id: decoded.company_id ?? null,
      clinic_ids: Array.isArray(decoded.clinic_ids) ? decoded.clinic_ids : [],
      permissions: [],
    };
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
}

function userFromProfile(profile: UserProfile): User {
  return {
    name: profile.name,
    email: profile.email,
    role: profile.role,
    level: profile.level,
    user_id: profile.id,
    company_id: profile.company_id ?? null,
    clinic_ids: profile.clinic_ids ?? [],
    permissions: profile.permissions ?? [],
  };
}

function loadStoredUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed as User;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [accessToken, setAccessToken] = useState<string | null>(() => {
    return localStorage.getItem('access_token');
  });
  const [user, setUser] = useState<User | null>(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return null;
    // Prefere user persistido (com permissions); fallback pra JWT
    return loadStoredUser() ?? userFromToken(token);
  });

  const login = (token: string, profile?: UserProfile | null) => {
    localStorage.removeItem('access_token');
    localStorage.removeItem(USER_STORAGE_KEY);
    setAccessToken(null);
    setUser(null);

    const nextUser = profile ? userFromProfile(profile) : userFromToken(token);
    if (!nextUser) {
      console.error('Token inválido ou profile ausente');
      return;
    }

    localStorage.setItem('access_token', token);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(nextUser));
    setAccessToken(token);
    setUser(nextUser);
  };

  const logout = () => {
    setAccessToken(null);
    setUser(null);
    localStorage.removeItem('access_token');
    localStorage.removeItem(USER_STORAGE_KEY);
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return false;

      const decoded: any = jwtDecode(token);
      const now = Date.now() / 1000;

      if (decoded.exp && decoded.exp < now) {
        logout();
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error refreshing token:', error);
      logout();
      return false;
    }
  };

  const can = (resource: string, action: string): boolean => {
    if (!user) return false;
    return user.permissions.includes(`${resource}.${action}`);
  };

  const canAny = (pairs: Array<[string, string]>): boolean => {
    if (!user) return false;
    return pairs.some(([r, a]) => user.permissions.includes(`${r}.${a}`));
  };

  // Aliases legacy — convertem pros novos roles via comparação de level/role
  // (mantidos só pra compatibilizar código ainda não migrado)
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const isAdminLocal =
    user?.role === 'company_admin' ||
    user?.role === 'clinic_manager' ||
    user?.role === 'admin_local';
  const canManageUsers = can('users', 'read');

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!accessToken,
        accessToken,
        user,
        can,
        canAny,
        isAdmin,
        isAdminLocal,
        canManageUsers,
        login,
        logout,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
